/** What a visit on the schedule is, in words and colour. Shared by the calendar and its detail panel. */
export interface ScheduleVisit {
  id: number;
  assignment_id: number;
  customer_name: string;
  client_id: number;
  client_name: string;
  date: string;
  start_time: string;
  end_time: string;
  locality: string;
  state: string;
  reason: string;
  lead_id: number;
  lead_name: string;
  assist_names: string[];
  contractor_ids: number[];
  team: string[];
  staffed: boolean;
  flags: string[];
  /** Who on the visit has marked time off then (with the time_off flag). */
  time_off_names?: string[];
}

export const CLOSED: Record<string, string> = {
  completed: 'Done',
  cancelled: 'Cancelled',
  missed: 'Missed',
  rescheduled: 'Rescheduled',
  assignment_ended: 'Assignment ended',
};

export const FLAG: Record<string, { label: string; tone: string; says: string }> = {
  no_lead: { label: 'No lead', tone: 'bad', says: 'No lead contractor is assigned yet.' },
  unconfirmed: {
    label: 'Not confirmed',
    tone: 'warn',
    says: 'The lead contractor has not confirmed this visit.',
  },
  clash: { label: 'Clash', tone: 'bad', says: 'A contractor on this visit is double-booked.' },
  time_off: {
    label: 'Time off',
    tone: 'bad',
    says: 'A contractor on this visit has marked time off then. Assign someone else.',
  },
  holiday: { label: 'Public holiday', tone: 'warn', says: 'This visit falls on a public holiday.' },
  changed: { label: 'Changed', tone: 'info', says: 'This visit was changed after it was made.' },
  notes: { label: 'Has notes', tone: 'info', says: 'This visit has notes.' },
};

/** What needs the office's eye, in the order they should be looked at. */
export const ATTENTION = ['no_lead', 'time_off', 'clash', 'unconfirmed'] as const;

export const isOpen = (v: Pick<ScheduleVisit, 'state'>) => v.state !== 'inactive';
export const needsAttention = (v: Pick<ScheduleVisit, 'state' | 'flags'>) =>
  isOpen(v) && ATTENTION.some((f) => v.flags.includes(f));

export type Tone = 'scheduled' | 'attention' | 'done' | 'closed';

export const TONES: { tone: Tone; label: string }[] = [
  { tone: 'scheduled', label: 'Scheduled' },
  { tone: 'attention', label: 'Needs attention' },
  { tone: 'done', label: 'Done' },
  { tone: 'closed', label: 'Cancelled or missed' },
];

/** One status per visit: the tone drives colour, the label is what people read. */
export function statusOf(v: Pick<ScheduleVisit, 'state' | 'reason' | 'flags'>): {
  tone: Tone;
  label: string;
} {
  if (!isOpen(v)) {
    return {
      tone: v.reason === 'completed' ? 'done' : 'closed',
      label: CLOSED[v.reason] ?? v.reason,
    };
  }
  return needsAttention(v)
    ? { tone: 'attention', label: 'Needs attention' }
    : { tone: 'scheduled', label: 'Scheduled' };
}

/** Every active contractor, with the days in the range they marked time off. From the schedule feed. */
export interface RosterEntry {
  id: number;
  name: string;
  time_off: { date: string; all_day: boolean; hours: string[] }[];
}

const overlaps = (aStart: string, aEnd: string, bStart: string, bEnd: string) =>
  aStart < bEnd && bStart < aEnd;

/** The time off someone marked on a date, in words: "Time off" for the day, or its hours. */
export function offLabel(r: RosterEntry, date: string): string {
  const off = r.time_off.find((t) => t.date === date);
  if (!off) return '';
  return off.all_day ? 'Time off' : `Off ${off.hours.join(', ')}`;
}

export type Availability = { kind: 'free' | 'busy' | 'off'; note: string };

/**
 * Whether someone could take a visit: off during its hours, already on another visit then
 * (a clash), or free, saying what else they have that day so the office can judge the travel.
 */
export function availability(
  r: RosterEntry,
  v: Pick<ScheduleVisit, 'id' | 'date' | 'start_time' | 'end_time'>,
  sameDay: ScheduleVisit[],
): Availability {
  const off = r.time_off.find((t) => t.date === v.date);
  if (off?.all_day) return { kind: 'off', note: 'Time off all day' };
  const hours = off?.hours ?? [];
  const offThen = hours.find((h) => {
    const [s = '', e = ''] = h.split('–');
    return overlaps(s, e, v.start_time, v.end_time);
  });
  if (offThen) return { kind: 'off', note: `Time off ${offThen}` };
  const theirs = sameDay.filter(
    (o) => o.id !== v.id && isOpen(o) && o.contractor_ids.includes(r.id),
  );
  const clash = theirs.find((o) => overlaps(o.start_time, o.end_time, v.start_time, v.end_time));
  if (clash) {
    return {
      kind: 'busy',
      note: `Busy ${clash.start_time}–${clash.end_time} · ${clash.customer_name}`,
    };
  }
  const also = [
    ...theirs.map((o) => `${o.start_time}–${o.end_time}`),
    ...hours.map((h) => `off ${h}`),
  ];
  return { kind: 'free', note: also.length ? `Free · also ${also.join(', ')}` : 'Free' };
}
